import { makeStyles } from '@material-ui/core/styles';
import React from 'react';

import { Grid } from '@material-ui/core';
import {
  HubContainerLinkBaseFragmentFragment,
  withUpdateHubAssignmentHoc,
  WithUpdateHubAssignmentHocChildProps,
} from '../../../api';
import { BaseForm, FormikWrapper, FormikWrapperHandlerProps } from '../../util/form2/BaseForm';
import { Button, ButtonSubmit } from '../../util/form2/Button';
import { ErrorBox } from '../../util/form2/ErrorBox';
import { updateValidationSchema, UpdateValues } from '../HubAssignmentForm';
import { SelectedHubType } from '../HubRemovalForm';
import { AssignHubFormFields } from './AssignHubFormFields';

/**
 * Styles for the form layout
 */
const useStyles = makeStyles({
  column: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    maxWidth: 320,
  },
});

/**
 * Form component for updating grain bin hub configuration.
 * Allows modification of hub settings including nickname, type, sensor configurations and measurements.
 *
 * @param updateHubAssignment - Function to update the hub assignment
 * @param grain_bin_hub - Current hub configuration data
 * @param onSubmitSuccess - Callback function executed after successful form submission
 * @param handleClose - Function to handle form closure
 * @param props - Additional form properties
 */
export const UpdateGrainBinHubFormBase = ({
  updateHubAssignment,
  grain_bin_hub,
  onSubmitSuccess,
  handleClose,
  ...props
}: WithUpdateHubAssignmentHocChildProps &
  FormikWrapperHandlerProps<UpdateValues, HubContainerLinkBaseFragmentFragment> & {
    grain_bin_hub: SelectedHubType;
    onSubmitSuccess: () => void;
    handleClose: () => void;
  }) => {
  const classes = useStyles();

  return (
    <FormikWrapper<UpdateValues, HubContainerLinkBaseFragmentFragment>
      {...props}
      validationSchema={updateValidationSchema}
      initialValues={{
        nickname: grain_bin_hub.nickname,
        hub_type: grain_bin_hub.hub_type,
        has_level_sensor: grain_bin_hub.has_level_sensor || false,
        hub_offset_ft: grain_bin_hub.hub_offset_ft,
        has_aux_level_sensor: grain_bin_hub.has_aux_level_sensor || false,
        sidewall_headspace_distance_ft:
          grain_bin_hub.sidewall_headspace_distance_ft !== null
            ? grain_bin_hub.sidewall_headspace_distance_ft * 12
            : 0,
        sidewall_auxilary_distance_ft:
          grain_bin_hub.sidewall_auxilary_distance_ft !== null
            ? grain_bin_hub.sidewall_auxilary_distance_ft * 12
            : 0,
        headspace_cablesensor_length_ft:
          grain_bin_hub.headspace_cablesensor_length_ft !== null
            ? grain_bin_hub.headspace_cablesensor_length_ft * 12
            : 0,
        aux_cablesensor_length_ft:
          grain_bin_hub.aux_cablesensor_length_ft !== null
            ? grain_bin_hub.aux_cablesensor_length_ft * 12
            : 0,
        hub_centerAxis_offset_ft: grain_bin_hub.hub_centerAxis_offset_ft,
        auxlevel_centerAxis_offset_ft: grain_bin_hub.auxlevel_centerAxis_offset_ft,
        auxlevel_offset_ft: grain_bin_hub.auxlevel_offset_ft,
        roof_angle_degrees: grain_bin_hub.roof_angle_degrees,
      }}
      onSubmit={(values) => {
        const {
          nickname,
          hub_type,
          has_level_sensor,
          hub_offset_ft,
          sidewall_headspace_distance_ft,
          sidewall_auxilary_distance_ft,
          headspace_cablesensor_length_ft,
          aux_cablesensor_length_ft,
          hub_centerAxis_offset_ft,
          auxlevel_centerAxis_offset_ft,
          auxlevel_offset_ft,
          roof_angle_degrees,
          has_aux_level_sensor,
        } = updateValidationSchema.validateSync(values);

        return updateHubAssignment({
          nickname,
          hub_type,
          has_level_sensor,
          hub_centerAxis_offset_ft,
          auxlevel_centerAxis_offset_ft,
          auxlevel_offset_ft,
          roof_angle_degrees,
          sidewall_headspace_distance_ft: sidewall_headspace_distance_ft
            ? sidewall_headspace_distance_ft / 12
            : null,
          sidewall_auxilary_distance_ft: sidewall_auxilary_distance_ft
            ? sidewall_auxilary_distance_ft / 12
            : null,
          headspace_cablesensor_length_ft: headspace_cablesensor_length_ft
            ? headspace_cablesensor_length_ft / 12
            : null,
          aux_cablesensor_length_ft: aux_cablesensor_length_ft
            ? aux_cablesensor_length_ft / 12
            : null,
          hub_container_link_id: grain_bin_hub.link_id,
          hub_offset_ft: has_level_sensor ? hub_offset_ft : 0,
          has_aux_level_sensor: has_aux_level_sensor || false,
        });
      }}
      onSubmitSuccess={onSubmitSuccess}
      render={() => (
        <BaseForm submitting_message="Updating Hub...">
          <div className={classes.column}>
            <AssignHubFormFields update />
            <Grid container spacing={2} alignItems="center" style={{ marginTop: '15px' }}>
              <Grid item xs={5}>
                <Button
                  variant="outlined"
                  fullWidth
                  style={{
                    margin: 0,
                    width: '100%',
                  }}
                  onClick={handleClose}
                >
                  Cancel
                </Button>
              </Grid>
              <Grid item xs={7}>
                <ButtonSubmit
                  style={{
                    margin: 0,
                    width: '100%',
                  }}
                  variant="contained"
                  color="primary"
                  allow_pristine
                  allow_btn_disabled_for_validation={false}
                >
                  Save Changes
                </ButtonSubmit>{' '}
              </Grid>
            </Grid>
          </div>
          <ErrorBox withoutGrid />
        </BaseForm>
      )}
    />
  );
};

/**
 * Enhanced version of UpdateGrainBinHubFormBase with hub assignment mutation capabilities
 */
export const UpdateGrainBinHubForm = withUpdateHubAssignmentHoc(UpdateGrainBinHubFormBase);
